import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Button as MantineButton } from "@mantine/core";
import { Link } from "react-router";

import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md";

interface ButtonBaseProps {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
}

type ButtonAsButton = ButtonBaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color" | "style" | keyof ButtonBaseProps> & {
    to?: undefined;
  };

type ButtonAsLink = ButtonBaseProps & {
  to: string;
  type?: undefined;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
};

type ButtonProps = ButtonAsButton | ButtonAsLink;

/**
 * App button, backed by Mantine (ADR-075).
 *
 * The public API stays the same as the hand-rolled one so feature screens do
 * not all have to change at once. Loading, focus rings and disabled state
 * come from Mantine; colour meaning still needs a label beside it (ADR-034).
 * Pass `to` to render as a React Router link with the same chrome.
 */
export function Button(props: ButtonProps) {
  const {
    variant = "primary",
    size = "md",
    loading = false,
    className,
    children,
    disabled,
  } = props;
  const mantineSize = size === "sm" ? "compact-sm" : "md";
  const isDisabled = disabled === true || loading;

  const color =
    variant === "primary" ? "brand" : variant === "danger" ? "danger" : variant === "ghost" ? "gray" : undefined;
  const mantineVariant =
    variant === "primary" || variant === "danger"
      ? "filled"
      : variant === "ghost"
        ? "subtle"
        : "default";

  if (props.to !== undefined) {
    return (
      <MantineButton
        component={Link}
        to={props.to}
        variant={mantineVariant}
        {...(color === undefined ? {} : { color })}
        size={mantineSize}
        loading={loading}
        disabled={isDisabled}
        className={cn(className)}
      >
        {children}
      </MantineButton>
    );
  }

  const { type = "button", onClick, ...rest } = props;

  return (
    <MantineButton
      type={type}
      variant={mantineVariant}
      {...(color === undefined ? {} : { color })}
      size={mantineSize}
      loading={loading}
      disabled={isDisabled}
      className={cn(className)}
      onClick={onClick}
      {...rest}
    >
      {children}
    </MantineButton>
  );
}
